/**
 * InputSystem - Touch/Mouse Input Handler
 * 
 * Handles grabbing, dragging, rotating, and throwing packages.
 * 
 * Key features:
 * - Grab offset preservation (grab where you touch, not center)
 * - Hybrid following: direct when unobstructed, spring-like when blocked
 * - Arc rotation via tangential velocity with warmup period
 * - Throw velocity from actual body physics velocity
 */

import { PhysicsWorld, PhysicsBody, BodyType } from './PhysicsWorld';

/** Input configuration */
export interface InputConfig {
    /** Spring stiffness for following (higher = more responsive) */
    springStiffness: number;
    /** Spring damping (higher = less oscillation, set near critical damping) */
    springDamping: number;
    /** Maximum velocity when following pointer (pixels/second) */
    maxFollowVelocity: number;
    /** Maximum throw speed in pixels/second */
    maxThrowSpeed: number;
    /** Minimum distance from grab point to detect rotation (pixels) */
    rotationMinRadius: number;
    /** Rotation sensitivity multiplier */
    rotationSensitivity: number;
    /** Minimum tangential speed to trigger rotation (pixels/frame) */
    rotationMinTangentSpeed: number;
    /** Cumulative tangential distance needed before rotation starts (pixels) */
    rotationWarmupDistance: number;
    /** Time in ms of consistent rotation direction before activating */
    rotationWarmupTime: number;
    /** Minimum pointer movement to consider (pixels) */
    minPointerMovement: number;
    /** Grace radius for grabbing nearby packages (pixels) */
    grabGraceRadius: number;
}

/** Default input configuration */
export const DEFAULT_INPUT_CONFIG: InputConfig = {
    // Reduced stiffness for gentler interaction with other packages
    springStiffness: 150,
    springDamping: 25,  // ~2*sqrt(150) for critical damping
    maxFollowVelocity: 4000,
    maxThrowSpeed: 2000,
    rotationMinRadius: 25,
    rotationSensitivity: 0.008,  // Lowered significantly
    rotationMinTangentSpeed: 3,
    rotationWarmupDistance: 25,
    rotationWarmupTime: 100,     // ms of consistent direction before rotation activates
    minPointerMovement: 2,
    grabGraceRadius: 30,  // Pixels of grace area around packages for grabbing
};

/** State of the currently held body */
interface HeldState {
    body: PhysicsBody;
    /** Offset from body center to grab point (in body-local coordinates) */
    grabOffsetLocal: { x: number; y: number };
    /** Current grab point in world coordinates (updated each frame) */
    grabPointWorld: { x: number; y: number };
    /** Previous pointer position (for tangential velocity calculation) */
    prevPointerX: number;
    prevPointerY: number;
    /** Target position (where we want the grab point to be) */
    targetX: number;
    targetY: number;
    /** Cumulative tangential movement for rotation warmup */
    rotationWarmup: number;
    /** Timestamp when rotation warmup started (for time-based warmup) */
    rotationWarmupStartTime: number;
    /** Direction of current rotation attempt (1 = CW, -1 = CCW, 0 = none) */
    rotationDirection: number;
    /** Whether rotation is currently active (past warmup) */
    rotationActive: boolean;
    /** Tracks if body is blocked (for hybrid following) */
    isBlocked: boolean;
    /** Smoothed blocked ratio for gradual transitions */
    blockedRatio: number;
    /** Original friction value to restore on release */
    originalFriction: number;
}

/**
 * InputSystem - Manages all pointer and keyboard input
 */
export class InputSystem {
    private physics: PhysicsWorld;
    private canvas: HTMLCanvasElement;
    private config: InputConfig;

    // Pointer state
    private pointerDown = false;
    private pointerX = 0;
    private pointerY = 0;

    // Held body state
    private held: HeldState | null = null;

    // Keyboard state
    private pendingKeyboardRotation = 0;

    // Callbacks
    private onGrab?: (body: PhysicsBody) => void;
    private onRelease?: (body: PhysicsBody, thrown: boolean) => void;

    constructor(
        physics: PhysicsWorld,
        canvas: HTMLCanvasElement,
        config: Partial<InputConfig> = {}
    ) {
        this.physics = physics;
        this.canvas = canvas;
        this.config = { ...DEFAULT_INPUT_CONFIG, ...config };

        this.setupEventListeners();
    }

    /**
     * Update scaled configuration values from layout.
     * Called after layout is calculated to apply responsive scaling.
     */
    setScaledConfig(values: {
        rotationMinTangentSpeed: number;
        rotationWarmupDistance: number;
        maxThrowSpeed: number;
    }): void {
        this.config.rotationMinTangentSpeed = values.rotationMinTangentSpeed;
        this.config.rotationWarmupDistance = values.rotationWarmupDistance;
        this.config.maxThrowSpeed = values.maxThrowSpeed;
    }

    /** Set callback for when a body is grabbed */
    setOnGrab(callback: (body: PhysicsBody) => void): void {
        this.onGrab = callback;
    }

    /** Set callback for when a body is released */
    setOnRelease(callback: (body: PhysicsBody, thrown: boolean) => void): void {
        this.onRelease = callback;
    }

    /** Get the currently held body (if any) */
    getHeldBody(): PhysicsBody | null {
        return this.held?.body ?? null;
    }

    /** Get spring line for rendering (returns null if body is at target) */
    getSpringLine(): { fromX: number; fromY: number; toX: number; toY: number } | null {
        if (!this.held) return null;

        const pos = this.physics.getPosition(this.held.body.handle);
        if (!pos) return null;

        const angle = this.physics.getRotation(this.held.body.handle) ?? 0;

        // Calculate current grab point position (body center + rotated offset)
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const grabX = pos.x + this.held.grabOffsetLocal.x * cos - this.held.grabOffsetLocal.y * sin;
        const grabY = pos.y + this.held.grabOffsetLocal.x * sin + this.held.grabOffsetLocal.y * cos;

        // Only show line if there's significant distance
        const dx = this.held.targetX - grabX;
        const dy = this.held.targetY - grabY;
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Show spring line when distance is noticeable (body is lagging behind)
        if (distance < 15) return null;

        return {
            fromX: grabX,
            fromY: grabY,
            toX: this.held.targetX,
            toY: this.held.targetY
        };
    }

    /** Check if input is enabled */
    private inputEnabled = true;

    /** Enable or disable input processing */
    setEnabled(enabled: boolean): void {
        this.inputEnabled = enabled;
        if (!enabled && this.held) {
            this.release();
        }
    }

    /** Setup DOM event listeners */
    private setupEventListeners(): void {
        // Pointer events (unified touch/mouse)
        this.canvas.addEventListener('pointerdown', this.onPointerDown);
        this.canvas.addEventListener('pointerup', this.onPointerUp);
        this.canvas.addEventListener('pointercancel', this.onPointerUp);
        this.canvas.addEventListener('pointermove', this.onPointerMove);
        this.canvas.addEventListener('pointerleave', this.onPointerUp);

        // Keyboard events
        window.addEventListener('keydown', this.onKeyDown);

        // Prevent context menu on right-click
        this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    }

    /** Clean up event listeners */
    destroy(): void {
        this.canvas.removeEventListener('pointerdown', this.onPointerDown);
        this.canvas.removeEventListener('pointerup', this.onPointerUp);
        this.canvas.removeEventListener('pointercancel', this.onPointerUp);
        this.canvas.removeEventListener('pointermove', this.onPointerMove);
        this.canvas.removeEventListener('pointerleave', this.onPointerUp);
        window.removeEventListener('keydown', this.onKeyDown);
    }

    /** Handle pointer down */
    private onPointerDown = (e: PointerEvent): void => {
        if (!this.inputEnabled) return;

        this.updatePointerPosition(e);
        this.pointerDown = true;
        this.tryGrab();
    };

    /** Handle pointer up */
    private onPointerUp = (_e: PointerEvent): void => {
        if (this.pointerDown) {
            this.pointerDown = false;
            if (this.held) {
                this.release();
            }
        }
    };

    /** Handle pointer move */
    private onPointerMove = (e: PointerEvent): void => {
        if (!this.inputEnabled) return;

        this.updatePointerPosition(e);

        if (this.pointerDown) {
            // If holding, update target
            if (this.held) {
                this.held.targetX = this.pointerX;
                this.held.targetY = this.pointerY;
            } else {
                // Drag-through grab: try to pick up if dragging over a package
                this.tryGrab();
            }
        }
    };

    /** Handle keyboard input */
    private onKeyDown = (e: KeyboardEvent): void => {
        if (!this.inputEnabled || !this.held) return;

        const ROTATION_STEP = Math.PI / 4; // 45 degrees

        switch (e.key.toLowerCase()) {
            case 'a':
            case 'arrowleft':
                this.pendingKeyboardRotation -= ROTATION_STEP;
                e.preventDefault();
                break;
            case 'd':
            case 'arrowright':
                this.pendingKeyboardRotation += ROTATION_STEP;
                e.preventDefault();
                break;
        }
    };

    /** Update pointer position from event */
    private updatePointerPosition(e: PointerEvent): void {
        const rect = this.canvas.getBoundingClientRect();
        this.pointerX = e.clientX - rect.left;
        this.pointerY = e.clientY - rect.top;
    }

    /** Try to grab a body at current pointer position */
    private tryGrab(): void {
        if (this.held) return;

        // First try exact point query
        let body = this.physics.queryPoint(this.pointerX, this.pointerY);
        let grabX = this.pointerX;
        let grabY = this.pointerY;
        
        // If no exact hit, try grace area query for nearest package
        if (!body) {
            const nearest = this.physics.queryNearestInRadius(
                this.pointerX, 
                this.pointerY, 
                this.config.grabGraceRadius
            );
            if (nearest) {
                body = nearest.body;
                // Grab at the closest point on the package, not where we touched
                grabX = nearest.closestX;
                grabY = nearest.closestY;
            }
        }
        
        if (!body) return;

        // Don't grab static bodies
        if (body.userData.shapeType === 'box' || 
            body.userData.shapeType === 'bag' || 
            body.userData.shapeType === 'triangle') {
            this.grab(body, grabX, grabY);
        }
    }

    /** Grab a specific body at a specific point */
    private grab(body: PhysicsBody, grabX: number, grabY: number): void {
        const pos = this.physics.getPosition(body.handle);
        const angle = this.physics.getRotation(body.handle);
        if (!pos || angle === null) return;

        // Calculate grab offset in world coordinates
        const grabOffsetWorld = {
            x: grabX - pos.x,
            y: grabY - pos.y
        };

        // Transform to body-local coordinates
        const cos = Math.cos(-angle);
        const sin = Math.sin(-angle);
        const grabOffsetLocal = {
            x: grabOffsetWorld.x * cos - grabOffsetWorld.y * sin,
            y: grabOffsetWorld.x * sin + grabOffsetWorld.y * cos
        };

        // Store original friction and set to 0 for easier movement
        const originalFriction = this.physics.getFriction(body.handle) ?? 1.0;
        this.physics.setFriction(body.handle, 0);

        this.held = {
            body,
            grabOffsetLocal,
            grabPointWorld: { x: grabX, y: grabY },
            prevPointerX: this.pointerX,
            prevPointerY: this.pointerY,
            targetX: this.pointerX,
            targetY: this.pointerY,
            rotationWarmup: 0,
            rotationWarmupStartTime: 0,
            rotationDirection: 0,
            rotationActive: false,
            isBlocked: false,
            blockedRatio: 0,
            originalFriction,
        };

        this.onGrab?.(body);
    }

    /** Release the held body */
    private release(): void {
        if (!this.held) return;

        const body = this.held.body;

        // Restore original friction
        this.physics.setFriction(body.handle, this.held.originalFriction);

        // Make sure body is dynamic before release
        this.physics.setBodyType(body.handle, BodyType.Dynamic);

        // Use the body's current physics velocity for throw (no multiplier)
        const vel = this.physics.getVelocity(body.handle);
        let thrown = false;
        
        if (vel) {
            const speed = Math.sqrt(vel.x * vel.x + vel.y * vel.y);
            if (speed > 100) { // Minimum speed to count as "thrown"
                let throwVx = vel.x;
                let throwVy = vel.y;
                
                // Clamp to max throw speed
                if (speed > this.config.maxThrowSpeed) {
                    const scale = this.config.maxThrowSpeed / speed;
                    throwVx *= scale;
                    throwVy *= scale;
                }
                
                this.physics.setVelocity(body.handle, throwVx, throwVy);
                thrown = true;
            }
        }

        this.onRelease?.(body, thrown);
        this.held = null;
        this.pendingKeyboardRotation = 0;
    }

    /** 
     * Update physics for held body - call this every physics step
     * 
     * Uses a critically-damped spring for following.
     * This naturally handles both cases:
     * - In air: force pushes body toward target
     * - When blocked: damping prevents jitter, spring relaxes naturally
     */
    update(dt: number): void {
        if (!this.held) return;

        const body = this.held.body;
        const pos = this.physics.getPosition(body.handle);
        const angle = this.physics.getRotation(body.handle);
        if (!pos || angle === null) return;

        // Calculate current grab point position (body center + rotated offset)
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        const grabX = pos.x + this.held.grabOffsetLocal.x * cos - this.held.grabOffsetLocal.y * sin;
        const grabY = pos.y + this.held.grabOffsetLocal.x * sin + this.held.grabOffsetLocal.y * cos;

        // Store current grab point for rotation calculation
        this.held.grabPointWorld.x = grabX;
        this.held.grabPointWorld.y = grabY;

        // Calculate displacement from grab point to target
        const dx = this.held.targetX - grabX;
        const dy = this.held.targetY - grabY;

        // Get current velocity
        const vel = this.physics.getVelocity(body.handle);
        const vx = vel?.x ?? 0;
        const vy = vel?.y ?? 0;

        // Spring physics: F = stiffness * displacement - damping * velocity
        // With critical damping, this is responsive in air
        // and naturally relaxes when blocked (can't move = velocity stays low = less damping force)
        const forceX = this.config.springStiffness * dx - this.config.springDamping * vx;
        const forceY = this.config.springStiffness * dy - this.config.springDamping * vy;
        
        let newVx = vx + forceX * dt;
        let newVy = vy + forceY * dt;

        // Clamp to max velocity
        const speed = Math.sqrt(newVx * newVx + newVy * newVy);
        if (speed > this.config.maxFollowVelocity) {
            const scale = this.config.maxFollowVelocity / speed;
            newVx *= scale;
            newVy *= scale;
        }

        this.physics.setVelocity(body.handle, newVx, newVy);

        // Calculate rotation from tangential pointer movement around GRAB POINT
        this.updateRotation();

        // Apply keyboard rotation
        if (this.pendingKeyboardRotation !== 0) {
            const newAngle = angle + this.pendingKeyboardRotation;
            this.physics.setRotation(body.handle, newAngle);
            this.physics.setAngularVelocity(body.handle, 0);
            this.pendingKeyboardRotation = 0;
        }

        // Update previous pointer position for next frame
        this.held.prevPointerX = this.pointerX;
        this.held.prevPointerY = this.pointerY;
    }

    /** 
     * Update rotation based on tangential velocity of pointer around GRAB POINT.
     * Uses BOTH distance AND time-based warmup to prevent accidental rotation.
     * Direction changes fully reset the warmup.
     */
    private updateRotation(): void {
        if (!this.held) return;

        const now = performance.now();

        // Calculate pointer movement this frame
        const pointerDx = this.pointerX - this.held.prevPointerX;
        const pointerDy = this.pointerY - this.held.prevPointerY;
        const pointerSpeed = Math.sqrt(pointerDx * pointerDx + pointerDy * pointerDy);
        
        // Skip if pointer didn't move enough
        if (pointerSpeed < this.config.minPointerMovement) return;

        // Calculate radius vector from GRAB POINT to current pointer position
        // This makes rotation feel more intuitive - rotate around where you're holding
        const grabPoint = this.held.grabPointWorld;
        const radiusX = this.pointerX - grabPoint.x;
        const radiusY = this.pointerY - grabPoint.y;
        const radiusLength = Math.sqrt(radiusX * radiusX + radiusY * radiusY);

        // Skip if too close to grab point (rotation becomes unstable)
        if (radiusLength < this.config.rotationMinRadius) {
            this.resetRotationState();
            return;
        }

        // Normalize radius vector
        const radiusNormX = radiusX / radiusLength;
        const radiusNormY = radiusY / radiusLength;

        // Calculate tangent vector (perpendicular to radius, counter-clockwise positive)
        const tangentX = -radiusNormY;
        const tangentY = radiusNormX;

        // Project pointer movement onto tangent to get tangential component
        const tangentSpeed = pointerDx * tangentX + pointerDy * tangentY;

        // Skip if tangential speed is too low (mostly radial movement)
        if (Math.abs(tangentSpeed) < this.config.rotationMinTangentSpeed) {
            // Decay warmup when not rotating
            this.held.rotationWarmup *= 0.7;
            if (this.held.rotationWarmup < 1) {
                this.resetRotationState();
            }
            return;
        }

        // Determine rotation direction (1 = positive/CCW, -1 = negative/CW)
        const newDirection = tangentSpeed > 0 ? 1 : -1;

        // Check for direction change - FULL RESET on any direction change
        if (this.held.rotationDirection !== 0 && this.held.rotationDirection !== newDirection) {
            // Direction changed - completely reset warmup state
            this.resetRotationState();
            // Don't process this frame, wait for consistent direction
            return;
        }

        // Start tracking time if this is a new rotation attempt
        if (this.held.rotationDirection === 0) {
            this.held.rotationWarmupStartTime = now;
        }

        // Update direction and accumulate warmup distance
        this.held.rotationDirection = newDirection;
        this.held.rotationWarmup += Math.abs(tangentSpeed);

        // Check if we've passed BOTH warmup thresholds (distance AND time)
        if (!this.held.rotationActive) {
            const distanceOk = this.held.rotationWarmup >= this.config.rotationWarmupDistance;
            const timeOk = (now - this.held.rotationWarmupStartTime) >= this.config.rotationWarmupTime;
            
            if (distanceOk && timeOk) {
                this.held.rotationActive = true;
            } else {
                return; // Still warming up, don't rotate yet
            }
        }

        // Calculate rotation amount
        const rotationAmount = tangentSpeed * this.config.rotationSensitivity;

        // Apply rotation
        const currentAngle = this.physics.getRotation(this.held.body.handle);
        if (currentAngle !== null) {
            this.physics.setRotation(this.held.body.handle, currentAngle + rotationAmount);
            this.physics.setAngularVelocity(this.held.body.handle, 0);
        }
    }

    /** Reset rotation tracking state */
    private resetRotationState(): void {
        if (!this.held) return;
        this.held.rotationWarmup = 0;
        this.held.rotationWarmupStartTime = 0;
        this.held.rotationDirection = 0;
        this.held.rotationActive = false;
    }
}
